<template>
	<div :class="{ 'dropzone-container': true, 'flex-box': true, 'minimal-dropzone': layout == 'minimal' }">
		<span class="title" v-if="label">{{ label }}</span>
		<hr v-if="label">
		<div :id="'dropzone-' + dropzoneId" class="flex-box dropzone-box" :class="[isDropZoneActive ? 'dropzone-active' : '']">
			<img :src="imageSource" v-if="imageSource" alt="" :class="{ 'dropzone-image': true, 'dropzone-image-svg': fileMetaText }" >
			<div class="dropzone-text flex-box" v-if="textVisible" >
				<span>Drag & Drop the desired file</span>
				<span>…or click to browse for a file instead.</span>
			</div>
			<div class="dropzone-text flex-box" v-if="!textVisible && fileMetaText" >
				<span>{{  fileMetaText }}</span>
			</div>
			<DxProgressBar class="dropzone-progress" :min="0" :max="100" width="30%" :show-status="false" :visible="progressVisible" :value="progressValue" />
		</div>
		<DxFileUploader :visible="false" :dialog-trigger="'#dropzone-' + dropzoneId" :drop-zone="'#dropzone-' + dropzoneId" :allowed-file-extensions="allowedExtensions ? allowedExtensions : []"
			:multiple="false" upload-mode="instantly" :upload-url="appInfo.baseURL + '/attachment'" :upload-headers="{ 'accept': 'application/json' }"
			@drop-zone-enter="onDropZoneEnter" @drop-zone-leave="onDropZoneLeave" @uploaded="onUploaded" @progress="onProgress" @upload-started="onUploadStarted"
		/>
	</div>
</template>

<script setup>
import { DxFileUploader } from 'devextreme-vue/file-uploader';
import { DxProgressBar } from 'devextreme-vue/progress-bar';
import { ref, defineProps, defineEmits, toRefs, onMounted } from 'vue';
import appInfo from "../app-info";

const props = defineProps([
	'label',					// Text label used for title (optional)
	'layout',					// Layout string, one of 'square' (default), 'minimal'.
	'initial',					// Initial attachment settings (for setting preview for existing data)
	'allowedExtensions'			// Array of allowed file extensions (optional), defaults to empty array
]);
const { label, layout } = toRefs(props);
const emits = defineEmits(["valueChanged"]);

const isDropZoneActive = ref(false);
const imageSource = ref('');
const textVisible = ref(true);
const progressVisible = ref(false);
const progressValue = ref(0);
const fileMetaText = ref('');

onMounted(() => {
	if (props.initial) {
		isDropZoneActive.value = false;
		imageSource.value = String(appInfo.storageURL + '/' + props.initial.store);
		textVisible.value = false;
		progressVisible.value = false;
		progressValue.value = 0;
		fileMetaText.value = '';
	}
});

// Generate a unique ID to be used as DOM ID for DxFileUploader.
const dropzoneId = ref(crypto.randomUUID().replaceAll('-', ''));

function onDropZoneEnter({ component, dropZoneElement, event }) {
	if (dropZoneElement.id.startsWith('dropzone-')) {
		const items = event.originalEvent.dataTransfer.items;
		const allowedFileExtensions = component.option('allowedFileExtensions');
		const draggedFileExtension = `.${items[0].type.replace(/^image\//, '')}`;
		const isSingleFileDragged = items.length === 1;
		const isValidFileExtension = allowedFileExtensions.includes(draggedFileExtension);

		if (isSingleFileDragged && isValidFileExtension) {
			isDropZoneActive.value = true;
		}
	}
}
function onDropZoneLeave(e) {
	if (e.dropZoneElement.id.startsWith('dropzone-')) {
		isDropZoneActive.value = false;
	}
}

function onUploaded({ file, request }) {
	const isImageFile = file.type && file.type.toLowerCase().startsWith('image/');
	if (isImageFile) {
		const fileReader = new FileReader();
		fileReader.onload = () => {
			isDropZoneActive.value = false;
			imageSource.value = String(fileReader.result);
		};
		fileReader.readAsDataURL(file);
	} else {
		imageSource.value = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzODQgNTEyIj48cGF0aCBkPSJNMzY5LjkgOTcuOUwyODYgMTRDMjc3IDUgMjY0LjggLS4xIDI1Mi4xLS4xSDQ4QzIxLjUgMCAwIDIxLjUgMCA0OHY0MTZjMCAyNi41IDIxLjUgNDggNDggNDhoMjg4YzI2LjUgMCA0OC0yMS41IDQ4LTQ4VjEzMS45YzAtMTIuNy01LTI1LTE0LjEtMzR6TTMzNiAxNDRIMjQ4Vjc1LjlsODggODguMXptLTQ4IDMyMEg0OFY0OGgxNjB2MTA0YzAgMTMuMyAxMC43IDI0IDI0IDI0aDEwNHYyODh6Ii8+PC9zdmc+';
		fileMetaText.value = file.name + ' (' + file.size + ')';
	}

	textVisible.value = false;
	progressVisible.value = false;
	progressValue.value = 0;

	let jsonResponse = JSON.parse(request.response);
	console.log(jsonResponse);
	emits('valueChanged', jsonResponse);
}
function onProgress(e) {
	progressValue.value = (e.bytesLoaded / e.bytesTotal) * 100;
}
function onUploadStarted() {
	imageSource.value = '';
	progressVisible.value = true;
	fileMetaText.value = '';
}
</script>

<style>
.dropzone-container > .title {
	font-size: 20px;
	margin-bottom: 0.5em;
	z-index: 1;
	background: var(--dx-color-main-bg);
	padding: 0 10px;
}
.dropzone-container > hr {
	width: 100%;
	margin-top: -1.75em;
	margin-bottom: 1.75em;
	border: 0;
	border-top: 1px solid black;
}
.flex-box {
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
}
.dropzone-box {
	width: 350px;
	height: 350px;
	background-color: rgb(183 183 183 / 10%);
	border-width: 2px;
	border-style: dashed;
	padding: 10px;
	border-color: var(--dx-color-border);
}
.dropzone-container.minimal-dropzone .dropzone-box {
	height: auto !important;
}
.dropzone-box > * {
	pointer-events: none;
}
.dropzone-box.dropzone-active {
	border-style: solid;
	border-color: var(--dx-color-primary);
}
.dropzone-image {
	max-width: 100%;
	max-height: 100%;
}
.dropzone-image-svg {
	max-height: 100px;
	margin-bottom: 5px;
}
.dropzone-text > span {
	font-weight: 100;
	opacity: 0.5;
}
.dropzone-progress {
	display: flex;
	margin-top: 10px;
}
</style>
